// frontend/src/components/ContactSection.js
import React, { useState, useContext, useEffect, useMemo } from 'react';
import axios from 'axios';
import AuthContext from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import { trackContactFormSubmit } from '../utils/analytics';
import {
  Box, Typography, TextField, Button, Alert, InputAdornment,
  CircularProgress, Stepper, Step, StepLabel, FormControl,
  RadioGroup, FormControlLabel, Radio, Card, CardContent, Checkbox, Chip
} from '@mui/material';
import { Phone } from '@mui/icons-material';
import {
  UserIcon as Person, EmailIcon as Email, SubjectIcon as Subject,
  MessageIcon as Message, SendIcon as Send, MailIcon as MailOutline,
  LocationIcon, TimeIcon, ExternalLinkIcon, PlusIcon, DeleteIcon, CheckCircleIcon, SparklesIcon
} from './Icons';

const DEFAULT_SHOP_ITEMS = [
  { id: 'kluft', name: 'Kluft', category: 'kluft', price: 35, priceFormatted: '35 €', hasSizes: true, sizes: ['128', '140', '152', '164', 'S', 'M', 'L', 'XL', 'XXL'], description: 'Offizielle Pfadfinder-Kluft', isCustomPrice: false },
  { id: 'halstuch', name: 'Halstuch', category: 'kleidung', price: 10, priceFormatted: '10 €', hasSizes: false, description: 'Pfadfinder-Halstuch', isCustomPrice: false },
  { id: 'knoten', name: 'Halstuchknoten', category: 'zubehoer', price: 4, priceFormatted: '4 €', hasSizes: false, description: 'Lederknoten für das Halstuch', isCustomPrice: false },
  { id: 'liederbuch', name: 'Liederbuch', category: 'zubehoer', price: 10, priceFormatted: '10 €', hasSizes: false, description: 'Liederbuch für Singerunde & Lagerfeuer', isCustomPrice: false },
  { id: 'juja', name: 'Juja', category: 'kleidung', price: 50, priceFormatted: '50 €', hasSizes: true, sizes: ['S', 'M', 'L', 'XL', 'XXL'], description: 'Traditionelle Jungenschaftsjacke (Juja) aus festem Kohte-Stoff', isCustomPrice: false },
  { id: 'sippen_abzeichen', name: 'Sippen-Abzeichen', category: 'abzeichen', price: 0, priceFormatted: 'Individuell', hasSizes: false, description: 'Individuell gestaltetes Abzeichen für deine Sippe', allowCustomNote: true, isCustomPrice: true }
];


const ContactSection = ({ activeWizard = 'contact', onWizardChange = () => {}, header, organization, contactForm }) => {
  const { PUBLIC_API_URL, fetchPublicData } = useContext(AuthContext);
  const { settings } = useSettings() || {};

  const effectiveOrg = organization || settings?.organization || {};
  const effectiveContactForm = contactForm || settings?.contactForm || {};
  const effectiveHeader = header || settings?.sections?.contact || {};

  const title = effectiveHeader?.title || 'Schreib uns!';
  const subtitle = effectiveHeader?.subtitle || 'Egal ob du reinschnuppern möchtest, eine Kluft oder Abzeichen bestellst oder Fragen hast – wir freuen uns von dir zu hören.';
  const contactEmail = effectiveContactForm?.recipientEmail || effectiveOrg?.contactEmail || 'kontakt@pfadfinder.local';

  // Configured values
  const availableSizes = useMemo(() => {
    return Array.isArray(effectiveContactForm?.kluftSizes) && effectiveContactForm.kluftSizes.length > 0
      ? effectiveContactForm.kluftSizes
      : ['128', '140', '152', '164', 'S', 'M', 'L', 'XL', 'XXL'];
  }, [effectiveContactForm?.kluftSizes]);

  const feedbackTopics = useMemo(() => {
    return Array.isArray(effectiveContactForm?.feedbackTopics) && effectiveContactForm.feedbackTopics.length > 0
      ? effectiveContactForm.feedbackTopics
      : ['Allgemeines Feedback', 'Lob & Anregungen', 'Frage zu Beiträgen & Kosten', 'Verhalten & Sicherheit', 'Website / Technisches', 'Sonstiges'];
  }, [effectiveContactForm?.feedbackTopics]);

  // Shop catalog items
  const shopCatalog = useMemo(() => {
    if (Array.isArray(effectiveContactForm?.shopItems) && effectiveContactForm.shopItems.length > 0) {
      return effectiveContactForm.shopItems;
    }
    return DEFAULT_SHOP_ITEMS;
  }, [effectiveContactForm?.shopItems]);

  // Available Wizards based on settings and configured tabsOrder
  const availableWizards = useMemo(() => {
    const isEnabled = (tabId) => {
      if (tabId === 'address') return effectiveContactForm?.enableAddressTab !== false;
      if (tabId === 'contact') return effectiveContactForm?.enableGeneral !== false;
      if (tabId === 'schnupper') return effectiveContactForm?.enableSchnupper !== false;
      if (tabId === 'kluftbestellung') return effectiveContactForm?.enableKluft !== false;
      if (tabId === 'beschwerde') return effectiveContactForm?.enableFeedback !== false;
      return true;
    };

    const getLabel = (tabId, tabName) => {
      if (tabId === 'address') return effectiveContactForm?.addressTabLabel || tabName || 'Adresse & Kontakt';
      if (tabId === 'contact') return tabName || 'Normale Nachricht';
      if (tabId === 'schnupper') return tabName || 'Schnuppern anfragen';
      if (tabId === 'kluftbestellung') return tabName || 'Kluft & Pfadiladen';
      if (tabId === 'beschwerde') return tabName || 'Feedback / Beschwerde';
      return tabName || tabId;
    };

    if (Array.isArray(effectiveContactForm?.tabsOrder) && effectiveContactForm.tabsOrder.length > 0) {
      const orderList = effectiveContactForm.tabsOrder
        .filter(t => isEnabled(t.id) && t.enabled !== false)
        .map(t => ({ id: t.id, label: getLabel(t.id, t.name || t.label) }));
      if (orderList.length > 0) return orderList;
    }

    const defaultTabs = [
      { id: 'address', label: effectiveContactForm?.addressTabLabel || 'Adresse & Kontakt' },
      { id: 'contact', label: 'Normale Nachricht' },
      { id: 'schnupper', label: 'Schnuppern anfragen' },
      { id: 'kluftbestellung', label: 'Kluft & Pfadiladen' },
      { id: 'beschwerde', label: 'Feedback / Beschwerde' }
    ];

    const fallbackList = defaultTabs.filter(t => isEnabled(t.id));
    return fallbackList.length > 0 ? fallbackList : [{ id: 'contact', label: 'Normale Nachricht' }];
  }, [effectiveContactForm]);

  // Auto-switch to first available wizard if current one is disabled
  useEffect(() => {
    if (availableWizards.length > 0 && !availableWizards.some(w => w.id === activeWizard)) {
      onWizardChange(availableWizards[0].id);
    }
  }, [activeWizard, availableWizards, onWizardChange]);

  // Common form state
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Schnupper state
  const [wizardData, setWizardData] = useState({
    childName: '',
    childAge: '',
    groupName: '',
    schnupperDate: 'nextWeekday',
    customDate: '',
    contactPhone: ''
  });
  const [activeStep, setActiveStep] = useState(0);
  const [events, setEvents] = useState([]);

  // Contact state
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');

  // Beschwerde state
  const [topic, setTopic] = useState('');

  // Shop / Pfadiladen order state
  const [cart, setCart] = useState({}); // { [itemId]: { quantity: number, size: string, customNote: string } }
  const [shopCategory, setShopCategory] = useState('all');
  const [shopGroup, setShopGroup] = useState('');
  const [shopPhone, setShopPhone] = useState('');
  const [shopPickup, setShopPickup] = useState(true);
  const [shopNotes, setShopNotes] = useState('');

  // Preview state
  const [showPreview, setShowPreview] = useState(false);
  const [finalMessage, setFinalMessage] = useState('');

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const data = await fetchPublicData('/events?upcoming=true&limit=10');
        setEvents(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Error fetching events:', err);
      }
    };
    fetchEvents();
  }, [fetchPublicData]);

  // Check URL query parameters for pre-filling
  useEffect(() => {
    if (typeof window !== 'undefined' && window.location.search) {
      const params = new URLSearchParams(window.location.search);
      const wizardParam = params.get('wizard');
      const groupParam = params.get('group');
      const ageParam = params.get('age');
      const categoryParam = params.get('category');

      if (wizardParam && ['address', 'schnupper', 'contact', 'kluftbestellung', 'beschwerde'].includes(wizardParam)) {
        onWizardChange(wizardParam);
      }
      if (groupParam || ageParam) {
        setWizardData(prev => ({
          ...prev,
          groupName: groupParam || prev.groupName,
          childAge: ageParam || prev.childAge
        }));
      }
      if (groupParam) {
        setShopGroup(groupParam);
      }
      if (categoryParam) {
        setShopCategory(categoryParam);
      }
    }
  }, [onWizardChange]);

  // Handle Bob fill events & Schnupper prefill events
  useEffect(() => {
    const handleBobFill = (e) => {
      const data = e.detail;
      if (data) {
        if (data.wizardType) {
          onWizardChange(data.wizardType);
          setShowPreview(false);
          setActiveStep(0);
          setError('');
          setSuccess('');
        }
        if (data.name) setName(data.name);
        if (data.email) setEmail(data.email);
        if (data.subject) setSubject(data.subject);
        if (data.message) setMessage(data.message);
        if (data.topic) setTopic(data.topic);
        if (data.childName || data.childAge || data.contactPhone || data.groupName || data.group) {
          setWizardData(prev => ({
            ...prev,
            childName: data.childName || prev.childName,
            childAge: data.childAge || prev.childAge,
            contactPhone: data.contactPhone || prev.contactPhone,
            groupName: data.groupName || data.group || prev.groupName
          }));
          if (data.groupName || data.group) {
            setShopGroup(data.groupName || data.group);
          }
        }
        
        const element = document.getElementById('contact-section');
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }
    };
    window.addEventListener('bob_fill_contact', handleBobFill);
    window.addEventListener('schnupper_fill_group', handleBobFill);
    return () => {
      window.removeEventListener('bob_fill_contact', handleBobFill);
      window.removeEventListener('schnupper_fill_group', handleBobFill);
    };
  }, [onWizardChange]);

  const steps = ['Kontaktdaten', 'Kindesinformationen', 'Schnuppertag', 'Bestätigung'];

  const handleWizardChange = (wizard) => {
    onWizardChange(wizard);
    setShowPreview(false);
    setActiveStep(0);
    setError('');
    setSuccess('');
  };

  // Helper for dynamic weekday calculation
  const getNextWeekdayDate = (dayIndex = 2) => {
    const today = new Date();
    const currentDay = today.getDay();
    const daysUntil = (dayIndex - currentDay + 7) % 7 || 7;
    const nextDate = new Date(today);
    nextDate.setDate(today.getDate() + daysUntil);
    return nextDate.toLocaleDateString('de-DE');
  };

  const weekdayMap = {
    nextSunday: { index: 0, name: 'Sonntag' },
    nextMonday: { index: 1, name: 'Montag' },
    nextTuesday: { index: 2, name: 'Dienstag' },
    nextWednesday: { index: 3, name: 'Mittwoch' },
    nextThursday: { index: 4, name: 'Donnerstag' },
    nextFriday: { index: 5, name: 'Freitag' },
    nextSaturday: { index: 6, name: 'Samstag' }
  };

  const configuredWeekdayKey = effectiveContactForm?.schnupperWeekday || null;
  const configuredWeekdayInfo = configuredWeekdayKey && weekdayMap[configuredWeekdayKey]
    ? weekdayMap[configuredWeekdayKey]
    : null;
  const nextWeekdayDateFormatted = configuredWeekdayInfo ? getNextWeekdayDate(configuredWeekdayInfo.index) : null;

  const getSelectedDateLabel = () => {
    if (wizardData.schnupperDate === 'nextWeekday' || wizardData.schnupperDate === 'nextTuesday') {
      return configuredWeekdayInfo
        ? `Nächsten ${configuredWeekdayInfo.name} (${nextWeekdayDateFormatted})`
        : 'Nächste reguläre Gruppenstunde';
    }
    if (wizardData.schnupperDate === 'custom') return wizardData.customDate || 'Eigenes Wunschdatum';
    return wizardData.schnupperDate || 'kein Datum ausgewählt';
  };

  // Cart operations
  const updateCartItemQuantity = (item, delta) => {
    setCart(prev => {
      const current = prev[item.id] || { quantity: 0, size: item.hasSizes ? (item.sizes?.[0] || availableSizes[0]) : '', customNote: '' };
      const newQty = Math.max(0, current.quantity + delta);
      if (newQty === 0) {
        const next = { ...prev };
        delete next[item.id];
        return next;
      }
      return {
        ...prev,
        [item.id]: {
          ...current,
          quantity: newQty,
          size: current.size || (item.hasSizes ? (item.sizes?.[0] || availableSizes[0]) : '')
        }
      };
    });
  };

  const updateCartItemField = (itemId, field, value) => {
    setCart(prev => {
      const current = prev[itemId] || { quantity: 1, size: '', customNote: '' };
      return {
        ...prev,
        [itemId]: {
          ...current,
          [field]: value
        }
      };
    });
  };

  // Calculate cart stats
  const cartSummary = useMemo(() => {
    let totalItems = 0;
    let totalPrice = 0;
    let hasCustomPrice = false;
    const selectedList = [];

    Object.entries(cart).forEach(([itemId, cData]) => {
      if (cData.quantity > 0) {
        const item = shopCatalog.find(i => i.id === itemId);
        if (item) {
          totalItems += cData.quantity;
          if (item.isCustomPrice) {
            hasCustomPrice = true;
          } else {
            totalPrice += (Number(item.price) || 0) * cData.quantity;
          }
          selectedList.push({
            ...item,
            quantity: cData.quantity,
            chosenSize: cData.size,
            customNote: cData.customNote
          });
        }
      }
    });

    return { totalItems, totalPrice, hasCustomPrice, selectedList };
  }, [cart, shopCatalog]);

  const generateMessage = () => {
    let generated = '';
    if (activeWizard === 'schnupper') {
      const dateLabel = getSelectedDateLabel();
      const groupText = wizardData.groupName ? ` in der Gruppe "${wizardData.groupName}"` : '';
      const contactPrompt = wizardData.contactPhone
        ? `Bitte meldet euch bei mir telefonisch unter ${wizardData.contactPhone} oder per E-Mail (${email}).`
        : `Bitte meldet euch bei mir per E-Mail (${email}).`;
      generated = `Hallo,\n\nich interessiere mich für einen Schnuppertag für mein Kind ${wizardData.childName || ''} (${wizardData.childAge ? `${wizardData.childAge} Jahre` : ''})${groupText} am ${dateLabel}.\n\n${contactPrompt}\n\nVielen Dank!`;
    } else if (activeWizard === 'contact') {
      generated = message;
    } else if (activeWizard === 'beschwerde') {
      generated = `Feedback / Beschwerde zum Thema: ${topic}\n\n${message}`;
    } else if (activeWizard === 'kluftbestellung') {
      const itemsText = cartSummary.selectedList.map((item, idx) => {
        const sizeInfo = item.hasSizes && item.chosenSize ? ` (Größe: ${item.chosenSize})` : '';
        const noteInfo = item.customNote ? ` [Angabe/Sippe: ${item.customNote}]` : '';
        const priceText = item.isCustomPrice ? 'Preis individuell' : `${item.price * item.quantity} € (${item.price} € / Stk.)`;
        return `${idx + 1}. ${item.quantity}x ${item.name}${sizeInfo}${noteInfo} — ${priceText}`;
      }).join('\n');

      const totalFormatted = cartSummary.hasCustomPrice 
        ? `${cartSummary.totalPrice} € (zzgl. individueller Sippen-Abzeichen)` 
        : `${cartSummary.totalPrice} €`;

      generated = `Kluft- & Pfadiladen-Bestellung:\n` +
        `-----------------------------------------\n` +
        `Besteller: ${name}\n` +
        `E-Mail: ${email}\n` +
        (shopPhone ? `Telefon: ${shopPhone}\n` : '') +
        (shopGroup ? `Zugehörige Gruppe / Sippe: ${shopGroup}\n` : '') +
        `Abholung: ${shopPickup ? 'Ja, im Pfadfinderheim / bei der Gruppenstunde' : 'Nach Vereinbarung'}\n\n` +
        `Bestellte Artikel (${cartSummary.totalItems} Stk.):\n` +
        `${itemsText}\n\n` +
        `Gesamtbetrag: ${totalFormatted}\n` +
        `-----------------------------------------\n` +
        (shopNotes ? `Anmerkungen / Wünsche:\n${shopNotes}\n\n` : '') +
        `Bitte gebt mir Bescheid, sobald die Sachen abholbereit sind und wie die Bezahlung erfolgen soll.`;
    }
    return generated;
  };

  const handleNext = () => {
    if (activeWizard === 'schnupper') {
      if (activeStep < 2) {
        setActiveStep(prev => prev + 1);
      } else {
        setFinalMessage(generateMessage());
        setShowPreview(true);
      }
    } else {
      setFinalMessage(generateMessage());
      setShowPreview(true);
    }
  };

  const handleBack = () => {
    if (showPreview) {
      setShowPreview(false);
    } else if (activeWizard === 'schnupper' && activeStep > 0) {
      setActiveStep(prev => prev - 1);
    }
  };

  const isFormComplete = () => {
    if (activeWizard === 'address') return true;
    if (!name || !email) return false;
    if (activeWizard === 'schnupper') {
      if (activeStep === 0) {
        if (effectiveContactForm?.phoneRequired && !wizardData.contactPhone) return false;
        return true;
      }
      if (activeStep === 1) return wizardData.childName && wizardData.childAge;
      if (activeStep === 2) return wizardData.schnupperDate && (wizardData.schnupperDate !== 'custom' || wizardData.customDate);
      return true;
    }
    if (activeWizard === 'contact') return subject && message;
    if (activeWizard === 'beschwerde') return topic && message;
    if (activeWizard === 'kluftbestellung') {
      if (cartSummary.totalItems === 0) return false;
      // Check if size is chosen for all items that require size
      return cartSummary.selectedList.every(i => !i.hasSizes || Boolean(i.chosenSize));
    }
    return false;
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    setError('');
    setSuccess('');
    setIsLoading(true);
    const submitSubject = activeWizard === 'contact' 
      ? subject 
      : `[${activeWizard === 'schnupper' ? 'Schnupperanfrage' : activeWizard === 'beschwerde' ? 'Feedback / Beschwerde' : 'Pfadiladen-Bestellung'}] ${shopGroup || wizardData.groupName ? `Gruppe ${shopGroup || wizardData.groupName}` : (subject || topic || 'Anfrage')}`;

    try {
      const response = await axios.post(`${PUBLIC_API_URL}/contact`, {
        name,
        email,
        subject: submitSubject,
        message: finalMessage,
        wizardType: activeWizard
      });

      trackContactFormSubmit();
      setSuccess(response.data.message || 'Ihre Anfrage wurde erfolgreich übermittelt! Wir melden uns in Kürze.');
      
      // Reset
      setName(''); setEmail(''); setSubject(''); setMessage(''); setTopic('');
      setWizardData({ childName: '', childAge: '', groupName: '', schnupperDate: 'nextWeekday', customDate: '', contactPhone: '' });
      setCart({});
      setShopNotes('');
      setShowPreview(false);
      setActiveStep(0);
    } catch (err) {
      console.error('Error sending message:', err);
      setError(err.response?.data?.message || 'Fehler beim Senden. Bitte versuchen Sie es erneut.');
    } finally {
      setIsLoading(false);
    }
  };

  // 1. Render Address & Meeting Place Tab
  const renderAddressTab = () => {
    const fullAddress = [effectiveOrg.addressStreet, effectiveOrg.addressCity].filter(Boolean).join(', ');
    const mapsLink = effectiveOrg.mapsUrl || (fullAddress ? `https://maps.google.com/?q=${encodeURIComponent(fullAddress)}` : null);

    return (
      <Box sx={{ mt: 1 }} className="space-y-6 animate-fade-in">
        <div className="border-b border-outlineVariant/30 pb-4">
          <Typography variant="h5" component="h3" fontWeight="bold" className="text-surface-on flex items-center gap-2">
            Treffpunkt &amp; Kontaktdaten
          </Typography>
          <p className="text-body-small text-surface-onVariant mt-1">
            Hier findest du unser Stammesheim, regelmäßige Gruppenstunden und alle Wege, uns direkt zu erreichen.
          </p>
        </div>

        {/* Location & Address Card (only rendered if location or address is configured) */}
        {(effectiveOrg.meetingPlace || fullAddress) && (
          <div className="p-5 rounded-2xl bg-surface-container border border-outlineVariant/40 space-y-3 shadow-xs">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary-container text-primary-onContainer flex items-center justify-center shrink-0 mt-0.5">
                <LocationIcon sx={{ fontSize: 22 }} />
              </div>
              <div className="flex-1">
                <span className="text-[11px] font-bold uppercase tracking-wider text-primary block">
                  Treffpunkt &amp; Pfadfinderheim
                </span>
                {effectiveOrg.meetingPlace && (
                  <p className="text-title-medium font-bold text-surface-on mt-0.5">
                    {effectiveOrg.meetingPlace}
                  </p>
                )}
                {fullAddress && (
                  <p className="text-body-medium text-surface-onVariant mt-1">
                    {fullAddress}
                  </p>
                )}
              </div>
            </div>

            {mapsLink && (
              <div className="pt-2">
                <a
                  href={mapsLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="md3-btn-tonal inline-flex items-center gap-2 text-xs font-bold py-2 px-4 rounded-full transition-transform hover:scale-105"
                >
                  <ExternalLinkIcon sx={{ fontSize: 16 }} />
                  <span>In Google Maps öffnen / Route planen</span>
                </a>
              </div>
            )}
          </div>
        )}

        {/* Meeting Times Card */}
        <div className="p-5 rounded-2xl bg-surface-container border border-outlineVariant/40 space-y-2 shadow-xs">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-secondary-container text-secondary-onContainer flex items-center justify-center shrink-0 mt-0.5">
              <TimeIcon sx={{ fontSize: 22 }} />
            </div>
            <div>
              <span className="text-[11px] font-bold uppercase tracking-wider text-secondary block">
                Reguläre Gruppenstunden &amp; Treffzeiten
              </span>
              <p className="text-title-small font-bold text-surface-on mt-0.5">
                {effectiveOrg.meetingTimes || 'Termine je nach Altersstufe (siehe Gruppenübersicht)'}
              </p>
              <p className="text-body-small text-surface-onVariant mt-1">
                Die genauen Zeiten und Altersstufen findest du auch in der Übersicht aller Gruppen.
              </p>
            </div>
          </div>
        </div>

        {/* Direct Communication Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {contactEmail && (
            <div className="p-4 rounded-xl bg-surface-containerLow border border-outlineVariant/30 flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-primary-container text-primary-onContainer flex items-center justify-center shrink-0">
                <Email sx={{ fontSize: 18 }} />
              </div>
              <div className="min-w-0 flex-1">
                <span className="text-[10px] uppercase font-bold text-surface-onVariant tracking-wider block">E-Mail</span>
                <a href={`mailto:${contactEmail}`} className="text-body-small font-medium text-surface-on hover:text-primary truncate block hover:underline">
                  {contactEmail}
                </a>
              </div>
            </div>
          )}

          {effectiveOrg.contactPhone && (
            <div className="p-4 rounded-xl bg-surface-containerLow border border-outlineVariant/30 flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-secondary-container text-secondary-onContainer flex items-center justify-center shrink-0">
                <Phone sx={{ fontSize: 18 }} />
              </div>
              <div className="min-w-0 flex-1">
                <span className="text-[10px] uppercase font-bold text-surface-onVariant tracking-wider block">Telefon / Notfall</span>
                <a href={`tel:${effectiveOrg.contactPhone}`} className="text-body-small font-medium text-surface-on hover:text-primary truncate block hover:underline">
                  {effectiveOrg.contactPhone}
                </a>
              </div>
            </div>
          )}
        </div>

        {/* Quick Action Navigation */}
        <div className="pt-3 border-t border-outlineVariant/30 space-y-2">
          <span className="text-label-small text-surface-onVariant font-bold uppercase tracking-wider block">
            Direkt ein Anliegen starten:
          </span>
          <div className="flex flex-wrap gap-2">
            {effectiveContactForm?.enableSchnupper !== false && (
              <Button
                variant="outlined"
                color="primary"
                onClick={() => handleWizardChange('schnupper')}
                sx={{ borderRadius: 100, textTransform: 'none', fontWeight: 'bold', fontSize: '0.82rem' }}
              >
                Schnuppern anfragen
              </Button>
            )}
            {effectiveContactForm?.enableKluft !== false && (
              <Button
                variant="outlined"
                color="primary"
                onClick={() => handleWizardChange('kluftbestellung')}
                sx={{ borderRadius: 100, textTransform: 'none', fontWeight: 'bold', fontSize: '0.82rem' }}
              >
                Kluft &amp; Pfadiladen
              </Button>
            )}
            {effectiveContactForm?.enableGeneral !== false && (
              <Button
                variant="outlined"
                color="primary"
                onClick={() => handleWizardChange('contact')}
                sx={{ borderRadius: 100, textTransform: 'none', fontWeight: 'bold', fontSize: '0.82rem' }}
              >
                Nachricht schreiben
              </Button>
            )}
          </div>
        </div>
      </Box>
    );
  };

  // 2. Render Schnupper Steps
  const renderSchnupperStep = (step) => {
    switch (step) {
      case 0:
        return (
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" component="h3" gutterBottom>Kontaktdaten (Eltern/Erziehungsberechtigte)</Typography>
            <TextField label="Dein Name" fullWidth required value={name} onChange={(e) => setName(e.target.value)} sx={{ mb: 2 }} InputProps={{ startAdornment: (<InputAdornment position="start"><Person className="text-surface-onVariant" /></InputAdornment>) }} />
            <TextField label="E-Mail" type="email" fullWidth required value={email} onChange={(e) => setEmail(e.target.value)} sx={{ mb: 2 }} InputProps={{ startAdornment: (<InputAdornment position="start"><Email className="text-surface-onVariant" /></InputAdornment>) }} />
            <TextField
              label={`Telefon ${effectiveContactForm?.phoneRequired ? '(erforderlich)' : '(optional)'}`}
              fullWidth
              required={effectiveContactForm?.phoneRequired === true}
              value={wizardData.contactPhone}
              onChange={(e) => setWizardData({...wizardData, contactPhone: e.target.value})}
              InputProps={{ startAdornment: (<InputAdornment position="start"><Phone className="text-surface-onVariant" /></InputAdornment>) }}
              helperText={effectiveContactForm?.phoneRequired ? 'Bitte Telefonnummer für Rückfragen angeben.' : ''}
            />
          </Box>
        );
      case 1:
        return (
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" component="h3" gutterBottom>Informationen zum Kind &amp; Gruppe</Typography>
            <TextField label="Name des Kindes" fullWidth required value={wizardData.childName} onChange={(e) => setWizardData({...wizardData, childName: e.target.value})} sx={{ mb: 2 }} placeholder="z.B. Lukas" />
            <TextField label="Alter des Kindes" type="number" fullWidth required value={wizardData.childAge} onChange={(e) => setWizardData({...wizardData, childAge: e.target.value})} sx={{ mb: 2 }} placeholder="z.B. 8" />
            <TextField
              label="Gewünschte Gruppe / Stufe (optional)"
              fullWidth
              value={wizardData.groupName}
              onChange={(e) => setWizardData({...wizardData, groupName: e.target.value})}
              placeholder="z.B. Meute, Wölflinge, Sippe..."
              helperText={wizardData.groupName ? `Vorausgewählt: ${wizardData.groupName}` : 'Falls du schon weißt, in welche Gruppe dein Kind möchte.'}
            />
          </Box>
        );
      case 2:
        return (
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" component="h3" gutterBottom>Wähle einen Schnuppertag</Typography>
            <FormControl component="fieldset" sx={{ mb: 2 }}>
              <RadioGroup value={wizardData.schnupperDate} onChange={(e) => setWizardData({...wizardData, schnupperDate: e.target.value})}>
                {events.slice(0, 5).map((event) => (
                  <FormControlLabel key={event._id} value={new Date(event.date).toLocaleDateString('de-DE')} control={<Radio />} label={`${event.title} - ${new Date(event.date).toLocaleDateString('de-DE')}`} />
                ))}
                {configuredWeekdayInfo ? (
                  <FormControlLabel
                    value="nextWeekday"
                    control={<Radio />}
                    label={`Nächsten ${configuredWeekdayInfo.name} (${nextWeekdayDateFormatted})`}
                  />
                ) : (
                  <FormControlLabel
                    value="nextWeekday"
                    control={<Radio />}
                    label="Nächste reguläre Gruppenstunde (nach Absprache)"
                  />
                )}
                <FormControlLabel value="custom" control={<Radio />} label="Eigenes Wunschdatum" />
              </RadioGroup>
            </FormControl>
            {wizardData.schnupperDate === 'custom' && (
              <TextField label="Eigenes Datum" type="date" fullWidth value={wizardData.customDate} onChange={(e) => setWizardData({...wizardData, customDate: e.target.value})} InputLabelProps={{ shrink: true }} />
            )}
          </Box>
        );
      default: return null;
    }
  };

  // 3. Render Normal Contact Form
  const renderContactForm = () => (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h6" component="h3" gutterBottom>Kontaktdaten &amp; Nachricht</Typography>
      <TextField label="Name" fullWidth required value={name} onChange={(e) => setName(e.target.value)} sx={{ mb: 2 }} InputProps={{ startAdornment: (<InputAdornment position="start"><Person className="text-surface-onVariant" /></InputAdornment>) }} />
      <TextField label="E-Mail" type="email" fullWidth required value={email} onChange={(e) => setEmail(e.target.value)} sx={{ mb: 2 }} InputProps={{ startAdornment: (<InputAdornment position="start"><Email className="text-surface-onVariant" /></InputAdornment>) }} />
      <TextField label="Betreff" fullWidth required value={subject} onChange={(e) => setSubject(e.target.value)} sx={{ mb: 2 }} InputProps={{ startAdornment: (<InputAdornment position="start"><Subject className="text-surface-onVariant" /></InputAdornment>) }} />
      <TextField label="Nachricht" fullWidth required multiline minRows={4} value={message} onChange={(e) => setMessage(e.target.value)} InputProps={{ startAdornment: (<InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1.5 }}><Message className="text-surface-onVariant" /></InputAdornment>) }} />
    </Box>
  );

  // 4. Render Feedback / Beschwerde Form
  const renderBeschwerdeForm = () => (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h6" component="h3" gutterBottom>Feedback &amp; Kontaktdaten</Typography>
      <TextField label="Name" fullWidth required value={name} onChange={(e) => setName(e.target.value)} sx={{ mb: 2 }} InputProps={{ startAdornment: (<InputAdornment position="start"><Person className="text-surface-onVariant" /></InputAdornment>) }} />
      <TextField label="E-Mail" type="email" fullWidth required value={email} onChange={(e) => setEmail(e.target.value)} sx={{ mb: 2 }} InputProps={{ startAdornment: (<InputAdornment position="start"><Email className="text-surface-onVariant" /></InputAdornment>) }} />
      <FormControl fullWidth sx={{ mb: 2 }}>
        <TextField
          select
          label="Thema"
          fullWidth
          required
          value={topic}
          onChange={(e) => setTopic(e.target.value)}
          SelectProps={{ native: true }}
          InputProps={{ startAdornment: (<InputAdornment position="start"><Subject className="text-surface-onVariant" /></InputAdornment>) }}
        >
          <option value="">Bitte Thema wählen...</option>
          {feedbackTopics.map((t, idx) => (
            <option key={idx} value={t}>{t}</option>
          ))}
        </TextField>
      </FormControl>
      <TextField label="Nachricht" fullWidth required multiline minRows={4} value={message} onChange={(e) => setMessage(e.target.value)} InputProps={{ startAdornment: (<InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1.5 }}><Message className="text-surface-onVariant" /></InputAdornment>) }} />
    </Box>
  );

  // 5. Render Pfadiladen & Kluftbestellung Form
  const renderKluftForm = () => {
    // Filter items by selected category
    const filteredItems = shopCategory === 'all' 
      ? shopCatalog 
      : shopCatalog.filter(item => item.category === shopCategory);

    return (
      <Box sx={{ mt: 1 }} className="space-y-6">
        <div>
          <Typography variant="h6" component="h3" fontWeight="bold">
            Pfadiladen &amp; Kluftbestellung
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
            Wähle deine Artikel aus dem Pfadiladen aus. Nach deiner Bestellung liegen die Sachen zur Abholung in der Gruppenstunde oder im Heim bereit.
          </Typography>
        </div>

        {/* Hinweis zur Beschaffung / Lieferzeit */}
        {effectiveContactForm?.showShopNotice && (effectiveContactForm?.shopNoticeText || '').trim() && (
          <div className="p-4 rounded-2xl bg-amber-500/10 border border-amber-500/30 text-amber-900 dark:text-amber-200 flex items-start gap-3 shadow-xs">
            <span className="text-xl leading-none shrink-0 mt-0.5 select-none">ℹ️</span>
            <div className="text-xs sm:text-sm leading-relaxed">
              <span className="font-bold block mb-0.5 text-amber-800 dark:text-amber-300">
                Wichtiger Hinweis zur Bestellung &amp; Beschaffung:
              </span>
              <p className="whitespace-pre-line">{effectiveContactForm.shopNoticeText}</p>
            </div>
          </div>
        )}

        {/* Customer Details */}
        <div className="p-4 rounded-2xl bg-surface-container border border-outlineVariant/30 space-y-3">
          <span className="text-label-small font-bold text-primary uppercase tracking-wider block">
            1. Kontaktdaten des Bestellers
          </span>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <TextField
              label="Dein Name"
              size="small"
              fullWidth
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              InputProps={{ startAdornment: (<InputAdornment position="start"><Person className="text-surface-onVariant" sx={{ fontSize: 18 }} /></InputAdornment>) }}
            />
            <TextField
              label="E-Mail"
              size="small"
              type="email"
              fullWidth
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              InputProps={{ startAdornment: (<InputAdornment position="start"><Email className="text-surface-onVariant" sx={{ fontSize: 18 }} /></InputAdornment>) }}
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <TextField
              label="Telefon (optional)"
              size="small"
              fullWidth
              value={shopPhone}
              onChange={(e) => setShopPhone(e.target.value)}
              InputProps={{ startAdornment: (<InputAdornment position="start"><Phone className="text-surface-onVariant" sx={{ fontSize: 18 }} /></InputAdornment>) }}
            />
            <TextField
              label="Gruppe / Sippe (optional)"
              size="small"
              fullWidth
              value={shopGroup}
              onChange={(e) => setShopGroup(e.target.value)}
              placeholder="z. B. Sippe Falken, Meute..."
              helperText={shopGroup ? `Zugeordnet: ${shopGroup}` : ''}
            />
          </div>
        </div>

        {/* Category Filter Pills */}
        <div className="space-y-2">
          <span className="text-label-small font-bold text-surface-onVariant uppercase tracking-wider block">
            2. Artikel auswählen
          </span>
          <div className="flex flex-wrap gap-1.5 pb-1">
            {[
              { id: 'all', label: 'Alle Artikel' },
              { id: 'kluft', label: 'Kluft' },
              { id: 'abzeichen', label: 'Abzeichen' },
              { id: 'kleidung', label: 'Kleidung' },
              { id: 'zubehoer', label: 'Liederbuch & Zubehör' }
            ].map(cat => (
              <button
                key={cat.id}
                type="button"
                onClick={() => setShopCategory(cat.id)}
                className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all ${
                  shopCategory === cat.id
                    ? 'bg-primary text-primary-on shadow-xs scale-105'
                    : 'bg-surface-container hover:bg-surface-containerHigh text-surface-onVariant'
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>

        {/* Catalog Item Cards */}
        <div className="space-y-3">
          {filteredItems.map(item => {
            const cartItem = cart[item.id] || { quantity: 0, size: '', customNote: '' };
            const isSelected = cartItem.quantity > 0;
            const itemSizes = item.hasSizes && Array.isArray(item.sizes) && item.sizes.length > 0 ? item.sizes : availableSizes;

            return (
              <div
                key={item.id}
                className={`p-4 rounded-2xl border transition-all ${
                  isSelected
                    ? 'bg-surface-containerHigh border-primary shadow-sm'
                    : 'bg-surface-containerLow border-outlineVariant/30 hover:border-outlineVariant/60'
                }`}
              >
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-title-small font-bold text-surface-on">{item.name}</span>
                      <span className={`px-2 py-0.5 rounded-md text-[11px] font-bold ${
                        item.isCustomPrice 
                          ? 'bg-secondary/15 text-secondary'
                          : 'bg-primary/10 text-primary'
                      }`}>
                        {item.priceFormatted || (item.price ? `${item.price} €` : 'Individuell')}
                      </span>
                      {isSelected && (
                        <span className="text-xs text-primary font-bold flex items-center gap-1">
                          <CheckCircleIcon sx={{ fontSize: 14 }} /> Im Warenkorb
                        </span>
                      )}
                    </div>
                    {item.description && (
                      <p className="text-body-small text-surface-onVariant mt-1">
                        {item.description}
                      </p>
                    )}
                  </div>

                  {/* Quantity Counter */}
                  <div className="flex items-center gap-2 self-end sm:self-center shrink-0">
                    <button
                      type="button"
                      onClick={() => updateCartItemQuantity(item, -1)}
                      disabled={cartItem.quantity === 0}
                      className="w-8 h-8 rounded-full bg-surface-container border border-outlineVariant/40 text-surface-on flex items-center justify-center font-bold text-sm hover:bg-surface-containerHighest disabled:opacity-30 disabled:pointer-events-none transition-colors"
                    >
                      −
                    </button>
                    <span className="w-7 text-center font-bold text-surface-on text-sm">
                      {cartItem.quantity}
                    </span>
                    <button
                      type="button"
                      onClick={() => updateCartItemQuantity(item, 1)}
                      className="w-8 h-8 rounded-full bg-primary text-primary-on flex items-center justify-center font-bold text-sm shadow-xs hover:opacity-90 transition-opacity"
                    >
                      +
                    </button>
                  </div>
                </div>

                {/* Additional Inputs if Selected (Size & Custom Note) */}
                {isSelected && (item.hasSizes || item.allowCustomNote) && (
                  <div className="mt-3 pt-3 border-t border-outlineVariant/30 flex flex-wrap gap-3 items-center">
                    {item.hasSizes && (
                      <div className="flex items-center gap-2">
                        <label className="text-xs font-bold text-surface-onVariant">Größe:</label>
                        <select
                          value={cartItem.size || itemSizes[0]}
                          onChange={(e) => updateCartItemField(item.id, 'size', e.target.value)}
                          className="bg-surface-containerLowest border border-outlineVariant/50 rounded-lg px-2.5 py-1 text-xs font-bold text-surface-on outline-none focus:border-primary"
                        >
                          {itemSizes.map((s, idx) => (
                            <option key={idx} value={s}>{s}</option>
                          ))}
                        </select>
                      </div>
                    )}

                    {item.allowCustomNote && (
                      <div className="flex-1 min-w-[200px]">
                        <input
                          type="text"
                          placeholder="Name / Motiv der Sippe (z. B. Sippe Falken)"
                          value={cartItem.customNote || ''}
                          onChange={(e) => updateCartItemField(item.id, 'customNote', e.target.value)}
                          className="w-full bg-surface-containerLowest border border-outlineVariant/50 rounded-lg px-3 py-1 text-xs text-surface-on outline-none focus:border-primary placeholder:text-surface-onVariant/40"
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Order Summary Box */}
        {cartSummary.totalItems > 0 && (
          <div className="p-5 rounded-2xl bg-surface-containerLow border border-outlineVariant/40 space-y-4 animate-fade-in shadow-xs">
            <div className="flex items-center justify-between border-b border-outlineVariant/30 pb-3">
              <div>
                <span className="text-label-small font-bold text-primary uppercase tracking-wider block">
                  Bestellübersicht
                </span>
                <p className="text-title-small font-bold text-surface-on mt-0.5">
                  {cartSummary.totalItems} Artikel im Warenkorb
                </p>
              </div>
              <div className="text-right">
                <span className="text-headline-small font-extrabold text-primary">
                  {cartSummary.totalPrice} €
                </span>
                {cartSummary.hasCustomPrice && (
                  <span className="text-[11px] text-surface-onVariant block opacity-80">
                    + individuelle Abzeichen
                  </span>
                )}
              </div>
            </div>

            <div className="space-y-1.5 text-xs text-surface-onVariant">
              {cartSummary.selectedList.map(item => (
                <div key={item.id} className="flex justify-between items-center py-1">
                  <span>
                    <strong>{item.quantity}x</strong> {item.name}
                    {item.hasSizes && item.chosenSize && ` (Gr. ${item.chosenSize})`}
                    {item.customNote && ` [${item.customNote}]`}
                  </span>
                  <span className="font-bold text-surface-on">
                    {item.isCustomPrice ? 'Individuell' : `${item.price * item.quantity} €`}
                  </span>
                </div>
              ))}
            </div>

            <div className="pt-2 border-t border-outlineVariant/30 space-y-2">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={shopPickup}
                    onChange={(e) => setShopPickup(e.target.checked)}
                    color="primary"
                    size="small"
                  />
                }
                label={<span className="text-xs font-medium text-surface-on">Abholung im Pfadfinderheim / bei der nächsten Gruppenstunde</span>}
              />

              <TextField
                label="Anmerkungen zur Bestellung (optional)"
                multiline
                rows={2}
                fullWidth
                size="small"
                value={shopNotes}
                onChange={(e) => setShopNotes(e.target.value)}
                placeholder="z. B. Besonderheiten, gewünschte Abholperson..."
              />
            </div>
          </div>
        )}
      </Box>
    );
  };

  const renderWizardForm = () => {
    if (activeWizard === 'address') return renderAddressTab();
    if (activeWizard === 'schnupper') return renderSchnupperStep(activeStep);
    if (activeWizard === 'contact') return renderContactForm();
    if (activeWizard === 'beschwerde') return renderBeschwerdeForm();
    if (activeWizard === 'kluftbestellung') return renderKluftForm();
    return null;
  };

  const renderFinalForm = () => (
    <Box sx={{ display: 'grid', gap: 3, mt: 2 }}>
      <Typography variant="subtitle1" fontWeight="bold">Bitte überprüfe deine Angaben und passe den Text bei Bedarf an:</Typography>
      <TextField label="Name" fullWidth required value={name} onChange={(e) => setName(e.target.value)} InputProps={{ startAdornment: (<InputAdornment position="start"><Person className="text-surface-onVariant" /></InputAdornment>) }} />
      <TextField label="E-Mail" type="email" fullWidth required value={email} onChange={(e) => setEmail(e.target.value)} InputProps={{ startAdornment: (<InputAdornment position="start"><Email className="text-surface-onVariant" /></InputAdornment>) }} />
      <TextField label="Nachricht" value={finalMessage} onChange={(e) => setFinalMessage(e.target.value)} multiline fullWidth minRows={8} />
    </Box>
  );

  return (
    <section id="contact-section" className="py-16 md:py-24 bg-surface section-reveal delay-400 scroll-mt-28">
      <div className="container mx-auto px-6 max-w-5xl">
        {/* Prominent Neuanmeldung & Schnuppern Callout Banner */}
        <div className="mb-10 bg-primary-container border border-primary/20 text-primary-onContainer rounded-3xl p-6 md:p-8 flex flex-col md:flex-row items-center justify-between gap-6 shadow-elevation-1">
          <div className="space-y-2 text-center md:text-left">
            <div className="inline-flex items-center gap-2 px-3.5 py-1 rounded-full bg-surface-containerHighest/80 text-primary-onContainer text-label-small font-bold border border-primary/20 shadow-xs">
              <SparklesIcon sx={{ fontSize: 14 }} />
              <span>Schnuppern &amp; Gruppen-Anmeldung</span>
            </div>
            <h3 className="text-title-large md:text-headline-small font-bold text-primary-onContainer">
              Du möchtest dein Kind anmelden oder reinschnuppern?
            </h3>
            <p className="text-body-medium text-primary-onContainer/85 max-w-2xl leading-relaxed">
              Nutze unser übersichtliches Neuanmeldungs-Center, um euer Kind für eine Schnupperstunde anzumelden oder wöchentliche Wunschzeiten anzugeben.
            </p>
          </div>
          <a
            href="/neuanmeldung"
            className="shrink-0 px-6 py-3.5 bg-primary text-primary-on hover:opacity-90 rounded-full font-bold text-label-large transition-all shadow-elevation-1 hover:shadow-elevation-2 hover:scale-105 flex items-center gap-2"
          >
            <span>Jetzt online anmelden</span>
            <ExternalLinkIcon sx={{ fontSize: 18 }} />
          </a>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Info Side */}
          <div>
            <div className="mb-6 inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary-container text-primary-onContainer shadow-elevation-1">
              <MailOutline sx={{ fontSize: 32 }} />
            </div>
            <div className="space-y-2 mb-8">
              <h2 className="text-headline-large md:text-display-small font-bold text-surface-on">{title}</h2>
              <p className="text-body-large text-surface-onVariant leading-relaxed">
                {subtitle}
              </p>
            </div>
            
            <div className="space-y-4">
               <div className="flex items-center gap-4 text-surface-onVariant mb-6">
                  <div className="w-10 h-10 rounded-full bg-primary-container text-primary-onContainer flex items-center justify-center">
                    <Email sx={{ fontSize: 20 }} />
                  </div>
                  <a href={`mailto:${contactEmail}`} className="hover:underline font-medium">{contactEmail}</a>
               </div>
               <div className="grid gap-3">
                 {availableWizards.map((w) => (
                   <Button
                     key={w.id}
                     variant={activeWizard === w.id ? 'contained' : 'outlined'}
                     color="primary"
                     onClick={() => handleWizardChange(w.id)}
                     sx={{ justifyContent: 'flex-start', textTransform: 'none', borderRadius: '1rem', py: 1.5, fontWeight: 'bold' }}
                   >
                     {w.label}
                   </Button>
                 ))}
               </div>
            </div>
          </div>

          {/* Form Side */}
          <div className="md3-card-elevated p-6 md:p-8 rounded-[2.5rem] bg-surface-containerLow border border-outlineVariant/30 shadow-elevation-1">
            {activeWizard === 'schnupper' && !showPreview && (
              <Stepper activeStep={activeStep} sx={{ mb: 4, flexWrap: 'wrap', gap: 1 }}>
                {steps.map((label) => (
                  <Step key={label}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>
            )}
            {activeWizard === 'schnupper' && showPreview && (
              <Stepper activeStep={3} sx={{ mb: 4, flexWrap: 'wrap', gap: 1 }}>
                {steps.map((label) => (
                  <Step key={label}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>
            )}

            {error && <Alert severity="error" sx={{ mb: 3, borderRadius: '1rem' }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 3, borderRadius: '1rem' }}>{success}</Alert>}

            {!showPreview ? renderWizardForm() : renderFinalForm()}

            {/* Bottom Actions */}
            {activeWizard === 'address' ? (
              <Box sx={{ display: 'flex', flexDirection: 'row', pt: 4, gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  onClick={() => handleWizardChange('contact')}
                  sx={{ borderRadius: 8, px: 3, fontWeight: 'bold', textTransform: 'none' }}
                >
                  Nachricht schreiben →
                </Button>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'row', pt: 4, gap: 2, justifyContent: 'space-between' }}>
                <Button
                  color="inherit"
                  disabled={!showPreview && (activeWizard !== 'schnupper' || activeStep === 0)}
                  onClick={handleBack}
                  sx={{ opacity: (!showPreview && (activeWizard !== 'schnupper' || activeStep === 0)) ? 0 : 1 }}
                >
                  Zurück
                </Button>
                {showPreview ? (
                  <Button variant="contained" onClick={handleSubmit} disabled={isLoading || !name || !email || !finalMessage} sx={{ borderRadius: 8, px: 4, fontWeight: 'bold' }}>
                    {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Abschicken'}
                  </Button>
                ) : (
                  <Button variant="contained" onClick={handleNext} disabled={!isFormComplete()} sx={{ borderRadius: 8, px: 4, fontWeight: 'bold' }}>
                    Weiter
                  </Button>
                )}
              </Box>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
